import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
    Play, Search, Lock, Landmark, Globe, 
    Sparkles, SlidersHorizontal, X 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClipCard = ({ clip, onClick }) => {
    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const isVault = clip.visibility_tier === 'vault';

    return (
        <div 
            className="card-base overflow-hidden cursor-pointer group animate-scale-in flex flex-col h-full"
            onClick={onClick}
            data-testid={`clip-card-${clip.id}`}
        >
            {/* Image Container */}
            <div className="aspect-video bg-muted relative overflow-hidden shrink-0">
                {clip.thumbnail_url ? (
                    <img 
                        src={clip.thumbnail_url} 
                        alt={clip.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-card">
                        <Play className="w-12 h-12 text-muted-foreground" />
                    </div>
                )}
                
                {/* Status Badges */}
                <div className="absolute top-2 left-2 flex gap-1 z-10">
                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold shadow-sm flex items-center gap-1 ${
                        isVault 
                            ? 'bg-purple-600 text-white' 
                            : 'bg-primary text-black'
                    }`}>
                        {isVault ? <Landmark className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                        {isVault ? 'Vault' : 'Featured'}
                    </span>
                </div>

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <Button className="btn-primary">
                        <Play className="w-4 h-4 mr-2" />
                        Create Remix
                    </Button>
                </div>

                {/* Duration badge */}
                <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs font-mono px-2 py-1 rounded">
                    {formatDuration(clip.duration)}
                </div>
            </div>
            
            {/* Text Content */}
            <div className="p-4 flex flex-col gap-1">
                <h3 className="font-heading text-sm uppercase tracking-wide truncate" title={clip.title}>
                    {clip.title}
                </h3>
                {clip.ai_title && (
                    <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed" title={clip.ai_title}>
                        <Sparkles className="w-3 h-3 inline mr-1 text-blue-400 shrink-0" />
                        {clip.ai_title}
                    </p>
                )}
            </div>
        </div>
    );
};

const ClipSkeleton = () => (
    <div className="card-base overflow-hidden">
        <Skeleton className="aspect-video" />
        <div className="p-4">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2 mt-2" />
        </div>
    </div>
);

export default function Gallery() {
    const navigate = useNavigate();
    const { isPro } = useAuth();
    
    const [clips, setClips] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Search & Filter State
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const [durationFilter, setDurationFilter] = useState('any');
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        fetchClips();
    }, []);

    const fetchClips = async () => {
        try {
            const response = await axios.get(`${API}/clips`);
            setClips(response.data);
        } catch (error) {
            console.error('Failed to fetch clips:', error);
        } finally {
            setLoading(false);
        }
    };

    // --- FILTER & SORT LOGIC ---
    
    // 1. Get all active clips
    const activeClips = clips.filter(c => c.status !== 'inactive');

    // 2. Define search & duration filters
    const matchesFilter = (clip) => {
        // Text Search
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            const matchesText = 
                clip.title?.toLowerCase().includes(q) || 
                clip.ai_title?.toLowerCase().includes(q) || 
                clip.tags?.some(tag => tag.toLowerCase().includes(q));
            
            if (!matchesText) return false;
        }

        // Duration Filter
        if (durationFilter === 'under_10' && clip.duration >= 10) return false;
        if (durationFilter === 'under_30' && clip.duration >= 30) return false;
        if (durationFilter === 'under_60' && clip.duration >= 60) return false;
        if (durationFilter === 'over_60' && clip.duration <= 60) return false;

        return true;
    };

    // 3. Apply Filters
    const filteredClips = activeClips.filter(matchesFilter);

    // 4. Apply Sort Logic
    const sortClips = (clipsArray) => {
        return [...clipsArray].sort((a, b) => {
            if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at);
            if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
            if (sortBy === 'shortest') return a.duration - b.duration;
            if (sortBy === 'longest') return b.duration - a.duration;
            return 0;
        });
    };

    // 5. Finalize the arrays
    const sortedAndFilteredClips = sortClips(filteredClips);
    
    const featuredClips = sortedAndFilteredClips.filter(c => c.visibility_tier === 'featured');
    const vaultClips = sortedAndFilteredClips.filter(c => c.visibility_tier === 'vault');
    
    // 6. Raw counts (ignoring search/filters) for headers
    const totalFeatured = activeClips.filter(c => c.visibility_tier === 'featured').length;
    const totalVault = activeClips.filter(c => c.visibility_tier === 'vault').length;

    const handleClipClick = (clip) => {
        navigate(`/editor/${clip.id}`);
    };

    // Helper to check if any non-default filters are active
    const hasActiveFilters = durationFilter !== 'any' || sortBy !== 'newest';

    return (
        <Layout>
            <div className="min-h-screen py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    
                    {/* --- PAGE HEADER --- */}
                    <div className="flex flex-col md:flex-row items-end justify-between gap-6 mb-6 border-b border-border pb-6">
                        <div className="w-full md:w-auto">
                            <h1 className="font-brand text-4xl md:text-5xl uppercase mb-4">
                                Clip <span className="text-primary">Gallery</span>
                            </h1>
                            <p className="text-muted-foreground max-w-2xl">
                                Browse our curated collection of public domain oddities. 
                                Remix them into something new.
                            </p>
                        </div>
                        
                        {/* Search Bar & Filter Toggle */}
                        <div className="flex w-full md:w-auto gap-2">
                            <div className="relative flex-1 md:w-64">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    type="text"
                                    placeholder="Search clips, tags, AI..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="input-base pl-9 h-11"
                                />
                            </div>
                            <Button 
                                variant={showFilters || hasActiveFilters ? "secondary" : "outline"} 
                                onClick={() => setShowFilters(!showFilters)} 
                                className="shrink-0 h-11"
                            >
                                <SlidersHorizontal className="w-4 h-4 mr-2" />
                                Filters
                            </Button>
                        </div>
                    </div>

                    {/* --- EXPANDABLE FILTERS PANEL --- */}
                    {showFilters && (
                        <div className="bg-muted/30 border border-border rounded-lg p-4 mb-10 flex flex-col sm:flex-row flex-wrap gap-4 animate-in slide-in-from-top-2">
                            
                            {/* Sort Dropdown */}
                            <div className="space-y-1.5 flex-1 min-w-[200px] max-w-xs">
                                <label className="text-[10px] font-heading uppercase tracking-wider text-muted-foreground">Sort By</label>
                                <Select value={sortBy} onValueChange={setSortBy}>
                                    <SelectTrigger className="w-full bg-background border-border">
                                        <SelectValue placeholder="Sort..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="newest">Newest Added</SelectItem>
                                        <SelectItem value="oldest">Oldest Added</SelectItem>
                                        <SelectItem value="shortest">Duration (Short to Long)</SelectItem>
                                        <SelectItem value="longest">Duration (Long to Short)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Duration Dropdown */}
                            <div className="space-y-1.5 flex-1 min-w-[200px] max-w-xs">
                                <label className="text-[10px] font-heading uppercase tracking-wider text-muted-foreground">Clip Duration</label>
                                <Select value={durationFilter} onValueChange={setDurationFilter}>
                                    <SelectTrigger className="w-full bg-background border-border">
                                        <SelectValue placeholder="Filter by duration..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="any">Any Length</SelectItem>
                                        <SelectItem value="under_10">Under 10 Seconds</SelectItem>
                                        <SelectItem value="under_30">Under 30 Seconds</SelectItem>
                                        <SelectItem value="under_60">Under 1 Minute</SelectItem>
                                        <SelectItem value="over_60">Over 1 Minute</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Clear Filters Button */}
                            {hasActiveFilters && (
                                <div className="flex items-end">
                                    <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => {
                                            setSortBy('newest');
                                            setDurationFilter('any');
                                        }}
                                        className="text-muted-foreground hover:text-foreground h-10"
                                    >
                                        <X className="w-4 h-4 mr-1" /> Clear
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* --- SECTION 1: FEATURED DROPS --- */}
                    <div className="mb-16">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="bg-primary/20 p-2 rounded-lg">
                                <Globe className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <h2 className="font-heading text-2xl uppercase tracking-wide">Featured Drops</h2>
                                <p className="text-xs text-muted-foreground">Free for everyone • {totalFeatured} clips</p>
                            </div>
                        </div>

                        {loading ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {[...Array(4)].map((_, i) => <ClipSkeleton key={i} />)}
                            </div>
                        ) : featuredClips.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {featuredClips.map((clip) => (
                                    <ClipCard 
                                        key={clip.id} 
                                        clip={clip} 
                                        onClick={() => handleClipClick(clip)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12 bg-muted/30 rounded-xl border border-dashed border-border">
                                <p className="text-muted-foreground">No featured clips match your search and filters.</p>
                                <Button variant="link" onClick={() => { setSearchQuery(''); setSortBy('newest'); setDurationFilter('any'); }} className="text-primary">Clear Filters</Button>
                            </div>
                        )}
                    </div>

                    {/* --- SECTION 2: THE VAULT --- */}
                    <div className="relative">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="bg-purple-500/20 p-2 rounded-lg">
                                <Landmark className="w-5 h-5 text-purple-400" />
                            </div>
                            <div>
                                <h2 className="font-heading text-2xl uppercase tracking-wide text-purple-100">The Vault</h2>
                                <p className="text-xs text-muted-foreground">Rare & Exclusive • {totalVault} clips</p>
                            </div>
                        </div>

                        {/* CONDITIONAL RENDER: Paywall vs Grid */}
                        {!isPro ? (
                            <div className="relative overflow-hidden rounded-xl border border-purple-500/30 bg-gradient-to-br from-purple-900/10 to-black p-8 md:p-12 text-center">
                                {/* Ambient Glow */}
                                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-full bg-purple-500/5 blur-[100px] pointer-events-none" />
                                
                                <div className="relative z-10 flex flex-col items-center">
                                    <div className="w-20 h-20 rounded-full bg-purple-500/10 flex items-center justify-center mb-6 animate-pulse-slow">
                                        <Lock className="w-10 h-10 text-purple-400" />
                                    </div>
                                    
                                    <h3 className="font-heading text-3xl uppercase mb-3 text-white">Unlock The Archives</h3>
                                    <p className="text-purple-200/70 max-w-lg mx-auto mb-8 text-lg">
                                        Gain instant access to our complete library of <strong>{totalVault} rare clips</strong>. 
                                        Join Pro to remix without limits.
                                    </p>
                                    
                                    <Button 
                                        className="h-12 px-8 text-base bg-purple-600 hover:bg-purple-700 text-white border-none shadow-[0_0_20px_rgba(147,51,234,0.3)] hover:shadow-[0_0_30px_rgba(147,51,234,0.5)] transition-all"
                                        onClick={() => navigate('/pricing')}
                                    >
                                        <Sparkles className="w-4 h-4 mr-2" />
                                        Get Pro Access
                                    </Button>
                                    
                                    <p className="mt-4 text-xs text-muted-foreground">
                                        Starting at just $9/month. Cancel anytime.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            /* PRO USER VIEW */
                            <>
                                {loading ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                        {[...Array(4)].map((_, i) => <ClipSkeleton key={i} />)}
                                    </div>
                                ) : vaultClips.length > 0 ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                        {vaultClips.map((clip) => (
                                            <ClipCard 
                                                key={clip.id} 
                                                clip={clip} 
                                                onClick={() => handleClipClick(clip)}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-12 bg-muted/30 rounded-xl border border-dashed border-border">
                                        <p className="text-muted-foreground">No vault clips match your search and filters.</p>
                                        <Button variant="link" onClick={() => { setSearchQuery(''); setSortBy('newest'); setDurationFilter('any'); }} className="text-primary">Clear Filters</Button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                </div>
            </div>
        </Layout>
    );
}